// Graphic plate packages and downloads. A package is one plate's background
// with one or two text areas — each common (the same words on every plate) or
// changing (each text's own words) — and up to 40 texts: 40 plates from one
// design, each downloaded as a picture or a video (16:9, 1920×1080), one by
// one or all together in a ZIP. Each text can have its own animation, font
// and length; a text's lines can each have their own size (in pixels) and
// colours (fill or gradient, stroke, shadow, 3D depth), words their own
// colour; a text area's look can be saved and used again on other plates;
// parts of the plate can be blurred.
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  FiX, FiPlus, FiTrash2, FiLoader, FiDownload, FiImage, FiFilm, FiSave, FiPackage, FiAlertTriangle, FiEye, FiEyeOff, FiClipboard, FiCheck, FiPlay, FiDroplet,
} from 'react-icons/fi';
import { fullStoryVideoService as api } from '../../services/apiService';
import { useT, useLanguageStore } from '../../utils/i18n';
import { useAuthStore } from '../../utils/store';
import { showError } from '../FullStory/shared';
import { BoxesEditor } from './UploadedPlates';

export const MAX_ITEMS = 40;
const MAX_TEXT = 300;
const MAX_LINES = 4;
const MAX_BLURS = 10;
const POLL_MS = 1500;
const SECONDS = [5, 8, 10, 15, 20, 30];
const INKS = ['auto', 'dark', 'light'];
const EFFECTS = ['flat', '3d'];
const ANIMS = ['wipe', 'fade', 'rise', 'pop', 'slide', 'none'];
// A line's colour looks (the swatch shown; solid: the colour it starts from when made custom).
const AUTO_SWATCH = 'conic-gradient(#fff 0 25%, #141210 0 50%, #fff 0 75%, #141210 0)';
export const LINE_COLORS = [
  ['white', '#FFFFFF', '#FFFFFF'], ['silver', 'linear-gradient(#FFFFFF, #9C9C9C)', '#EDEDED'], ['gold', 'linear-gradient(#FFF6A8, #E58E00)', '#FFC93C'],
  ['yellow', '#FFE100', '#FFE100'], ['red', '#E3262B', '#E3262B'], ['black', '#141210', '#141210'], ['blue', '#2F6BFF', '#2F6BFF'],
];
// More colours to pick from at a click (a broadcast palette); any other by picker, hex or RGB.
const PALETTE = [
  '#FFFFFF', '#D9D9D9', '#8C8C8C', '#3D3D3D', '#000000', '#FFF3B0', '#FFE100', '#FFC93C', '#FF9F1C', '#FF6A00',
  '#E3262B', '#B00012', '#7A0010', '#FF4F9A', '#C2185B', '#8E24AA', '#4A148C', '#0A3AA6', '#2F6BFF', '#4FC3F7',
  '#00BCD4', '#009688', '#00C853', '#1B5E20', '#8BC34A', '#795548', '#3B1D00', '#160402',
];
// A line's size: automatic (as large as fits the area), or pixels at 1920×1080.
const FONT_PX = [10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 44, 46, 48, 50, 54, 58, 62, 66, 70, 76, 82, 88, 96, 104, 112, 120, 132, 144, 160, 180, 200];
// Sizes against the text's other lines (packages saved before sizes in pixels).
const OLD_SIZES = ['s', 'l', 'xl'];
const BLUR_SHAPES = ['rect', 'rounded', 'square', 'ellipse', 'circle'];
const BLUR_STRENGTHS = ['light', 'medium', 'strong', 'pixelate'];
const BLUR_EDGES = [['sharp', 0], ['soft', 0.35], ['softer', 0.8]];
const MANAGERS = ['admin', 'superadmin', 'manager'];

// The shared .input style sets a full width and roomy padding over Tailwind's
// utilities: compact controls (in a row) say so themselves.
const COMPACT = { width: 'auto', paddingTop: '0.25rem', paddingBottom: '0.25rem', paddingLeft: '0.5rem', paddingRight: '2rem' };

/** Save a file the server has ready (the token is in the URL: a plain link works). */
function save(url) {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  a.download = '';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Downloads that take a while (videos, ZIPs): start(templateId, body) asks the
 * server to make it, follows it, and saves the file when it's ready — or, with
 * onReady(url), hands it over instead (a preview to play).
 * job: { kind, status, done, total, error } while one is under way (or failed).
 */
export const useExport = ({ onReady = null } = {}) => {
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  const alive = useRef(true);
  const ready = useRef(onReady);
  ready.current = onReady;
  // Mounted again (React's development double mount, too): following resumes.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; clearTimeout(timer.current); };
  }, []);

  const follow = useCallback(async (id) => {
    try {
      const next = await api.plateExport(id);
      if (!alive.current) return;
      if (next.status === 'ready') {
        setJob(null);
        if (ready.current) ready.current(api.plateExportUrl(id)); else save(api.plateExportUrl(id));
      } else {
        setJob(next);
        if (next.status === 'working') timer.current = setTimeout(() => follow(id), POLL_MS);
      }
    } catch (e) {
      if (alive.current) setJob({ status: 'failed', error: e?.response?.data?.message || e.message, done: 0, total: 0 });
    }
  }, []);

  const start = useCallback(async (templateId, body) => {
    clearTimeout(timer.current);
    try {
      const started = await api.startPlateExport(templateId, body);
      setJob(started);
      follow(started.id);
    } catch (e) {
      showError(e);
    }
  }, [follow]);

  return { job, busy: job?.status === 'working', start, clear: () => setJob(null) };
};

/** A job's progress line: "Making videos… 3 / 12", or why it failed. */
const ExportStatus = ({ job, onClear }) => {
  const t = useT();
  if (!job) return null;
  if (job.status === 'failed') {
    return (
      <p className="inline-flex items-center gap-1.5 text-xs text-red-700">
        <FiAlertTriangle size={13} /> {job.error || t('plateTemplates.download.failed')}
        {onClear && <button type="button" onClick={onClear} className="underline ml-1">{t('plateTemplates.close')}</button>}
      </p>
    );
  }
  const pct = job.total ? Math.round((job.done / job.total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-xs text-gray-600 min-w-[12rem]">
      <FiLoader size={13} className="animate-spin flex-shrink-0" />
      <span className="whitespace-nowrap">{t(`plateTemplates.download.making.${job.kind}`, { done: job.done, total: job.total })}</span>
      <span className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden min-w-[3rem]"><span className="block h-full bg-primary-600 transition-all" style={{ width: `${pct}%` }} /></span>
    </div>
  );
};

/**
 * A plate's downloads, all 16:9 at 1920×1080: its background alone, the plate
 * as a picture, and as a video. (An uploaded or created plate's picture is
 * its background: no words — words come with a package.)
 */
export const PlateDownloads = ({ template }) => {
  const t = useT();
  const { job, busy, start, clear } = useExport();
  const text = Boolean(template.uploaded);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-gray-700 font-medium inline-flex items-center gap-1.5"><FiDownload size={14} /> {t('plateTemplates.download.title')}</span>
      <a href={api.plateTemplateUrl(template.id, 'background.jpg', template.updatedAt, { download: true })} className="btn btn-outline btn-sm flex items-center gap-1" download>
        <FiImage size={14} /> {t('plateTemplates.download.background')}
      </a>
      {!text && (
        <a href={api.plateTemplateUrl(template.id, 'fullhd.jpg', template.updatedAt, { download: true })} className="btn btn-outline btn-sm flex items-center gap-1" download>
          <FiImage size={14} /> {t('plateTemplates.download.image')}
        </a>
      )}
      <button type="button" onClick={() => start(template.id, { kind: 'video' })} disabled={busy} className="btn btn-outline btn-sm flex items-center gap-1">
        {busy ? <FiLoader size={14} className="animate-spin" /> : <FiFilm size={14} />} {t('plateTemplates.download.video')}
      </button>
      <ExportStatus job={job} onClear={clear} />
    </div>
  );
};

// ─── Shared pieces: the quick preview, blur areas, line styles ──────────────

let fontsPromise = null;
export const loadFonts = () => {
  if (!fontsPromise) fontsPromise = api.plateFonts().catch((e) => { fontsPromise = null; throw e; });
  return fontsPromise;
};

/**
 * A plate's quick preview picture for `body` ({ areas, texts, font, blurs,
 * plateBlurs, noText }), redrawn a moment after each change (the server takes
 * a few hundred milliseconds at most). Returns { url, loading, error }.
 */
export const useStillPreview = (plateId, body, enabled = true) => {
  const [state, setState] = useState({ url: null, loading: false, error: '' });
  const key = JSON.stringify(body);
  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    setState((p) => ({ ...p, loading: true, error: '' }));
    const timer = setTimeout(async () => {
      try {
        const blob = await api.platePackagePreview(plateId, JSON.parse(key), { signal: controller.signal });
        const url = URL.createObjectURL(blob);
        setState((p) => { if (p.url) URL.revokeObjectURL(p.url); return { url, loading: false, error: '' }; });
      } catch (e) {
        if (controller.signal.aborted) return;
        let message = e.message;
        try { message = JSON.parse(await e?.response?.data?.text?.())?.message || message; } catch { /* not JSON */ }
        setState((p) => ({ ...p, loading: false, error: message }));
      }
    }, 150);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [plateId, key, enabled]);
  useEffect(() => () => { if (state.url) URL.revokeObjectURL(state.url); }, [state.url]);
  return state;
};

/** The preview picture, with a "drawing" tag while it's being redrawn. */
export const StillPreview = ({ preview, label, children }) => {
  const t = useT();
  return (
    <div className="relative aspect-video bg-gray-900 rounded-xl overflow-hidden">
      {preview.url && <img src={preview.url} alt={label} className="absolute inset-0 w-full h-full object-cover" />}
      {preview.loading && <span className="absolute top-2 right-2 inline-flex items-center gap-1.5 rounded-full bg-black/65 text-white text-[11px] px-2.5 py-0.5"><FiLoader size={11} className="animate-spin" /> {t('plateTemplates.pkg.drawing')}</span>}
      {preview.error && <span className="absolute inset-x-2 bottom-2 rounded bg-black/75 text-white text-xs px-2 py-1">{preview.error}</span>}
      {children}
    </div>
  );
};

/** Show the plate with or without its words (a switch). */
export const TextToggle = ({ on, onChange }) => {
  const t = useT();
  return (
    <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border ${on ? 'bg-white border-gray-200 text-gray-700' : 'bg-gray-800 border-gray-800 text-white'}`}>
      {on ? <FiEye size={13} /> : <FiEyeOff size={13} />} {t(on ? 'plateTemplates.pkg.withText' : 'plateTemplates.pkg.withoutText')}
    </button>
  );
};

/** A new blur area of a shape, in the middle of the plate. */
const newBlur = (shape) => {
  const w = shape === 'square' || shape === 'circle' ? 0.14 : 0.22;
  const h = shape === 'square' || shape === 'circle' ? (w * 16) / 9 : 0.22;
  return { shape, x: Number((0.5 - w / 2).toFixed(3)), y: Number((0.45 - h / 2).toFixed(3)), w, h, strength: 'medium', feather: 0.35 };
};

/** Blur areas as the box editor shows them (after the text areas). */
export const blurBoxes = (blurs, t) => blurs.map((b, i) => ({
  box: b,
  label: `${t('plateTemplates.blur.label', { n: i + 1 })} · ${t(`plateTemplates.blur.shape.${b.shape}`)}`,
  tone: 'blur',
  shape: b.shape === 'circle' || b.shape === 'ellipse' ? 'ellipse' : b.shape === 'rounded' ? 'rounded' : 'rect',
  lock: b.shape === 'square' || b.shape === 'circle',
}));

const ShapeIcon = ({ shape }) => {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 2 };
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      {shape === 'rect' && <rect x="2" y="6" width="20" height="12" {...common} />}
      {shape === 'rounded' && <rect x="2" y="6" width="20" height="12" rx="4" {...common} />}
      {shape === 'square' && <rect x="5" y="5" width="14" height="14" {...common} />}
      {shape === 'ellipse' && <ellipse cx="12" cy="12" rx="10" ry="6" {...common} />}
      {shape === 'circle' && <circle cx="12" cy="12" r="8" {...common} />}
    </svg>
  );
};

/**
 * Blur areas: add one by shape (it appears on the plate, to move and resize),
 * then choose how strong and how soft its edge is. onAdded() shows the areas.
 */
export const BlurControls = ({ blurs, onChange, onAdded }) => {
  const t = useT();
  const set = (i, change) => onChange(blurs.map((b, k) => (k === i ? { ...b, ...change } : b)));
  const changeShape = (i, shape) => {
    const b = blurs[i];
    // Square and circle keep their sides equal.
    const h = shape === 'square' || shape === 'circle' ? Math.min((b.w * 16) / 9, 1 - b.y) : b.h;
    const w = shape === 'square' || shape === 'circle' ? (h * 9) / 16 : b.w;
    set(i, { shape, w, h });
  };
  return (
    <section className="space-y-2" aria-label={t('plateTemplates.blur.title')}>
      {/* One compact row: the shapes as small icons (their names on hover). */}
      <div className="flex flex-wrap items-center gap-1" title={t('plateTemplates.blur.hint')}>
        <p className="text-xs font-semibold text-gray-700 mr-1 inline-flex items-center gap-1"><FiDroplet size={13} /> {t('plateTemplates.blur.title')}</p>
        {BLUR_SHAPES.map((shape) => {
          const label = t('plateTemplates.blur.add', { shape: t(`plateTemplates.blur.shape.${shape}`) });
          return (
            <button
              key={shape}
              type="button"
              disabled={blurs.length >= MAX_BLURS}
              onClick={() => { onChange([...blurs, newBlur(shape)]); onAdded?.(); }}
              title={label}
              aria-label={label}
              className="w-7 h-7 rounded-md border border-gray-200 bg-white text-gray-600 hover:border-sky-400 hover:text-sky-700 flex items-center justify-center disabled:opacity-40"
            >
              <ShapeIcon shape={shape} />
            </button>
          );
        })}
      </div>
      {blurs.length > 0 && (
        <ul className="space-y-1.5">
          {blurs.map((b, i) => (
            <li key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 bg-sky-50/50 px-2 py-1.5 text-xs">
              <span className="font-medium text-sky-800 w-14">{t('plateTemplates.blur.label', { n: i + 1 })}</span>
              <select value={b.shape} onChange={(e) => changeShape(i, e.target.value)} aria-label={t('plateTemplates.blur.shapeLabel')} style={COMPACT} className="input text-xs py-1 w-auto">
                {BLUR_SHAPES.map((s) => <option key={s} value={s}>{t(`plateTemplates.blur.shape.${s}`)}</option>)}
              </select>
              <select value={b.strength} onChange={(e) => set(i, { strength: e.target.value })} aria-label={t('plateTemplates.blur.strengthLabel')} style={COMPACT} className="input text-xs py-1 w-auto">
                {BLUR_STRENGTHS.map((s) => <option key={s} value={s}>{t(`plateTemplates.blur.strength.${s}`)}</option>)}
              </select>
              <select value={BLUR_EDGES.reduce((best, [id, v]) => (Math.abs(v - b.feather) < Math.abs(BLUR_EDGES.find((e) => e[0] === best)[1] - b.feather) ? id : best), 'soft')} onChange={(e) => set(i, { feather: BLUR_EDGES.find(([id]) => id === e.target.value)[1] })} aria-label={t('plateTemplates.blur.edgeLabel')} style={COMPACT} className="input text-xs py-1 w-auto">
                {BLUR_EDGES.map(([id]) => <option key={id} value={id}>{t(`plateTemplates.blur.edge.${id}`)}</option>)}
              </select>
              <button type="button" onClick={() => onChange(blurs.filter((_, k) => k !== i))} aria-label={t('plateTemplates.blur.remove')} title={t('plateTemplates.blur.remove')} className="btn btn-ghost btn-sm px-1.5 text-red-600 ml-auto"><FiTrash2 size={13} /></button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

// ─── Colours ─────────────────────────────────────────────────────────────

/** What someone typed as a colour — #RGB, #RRGGBB, rgb(r, g, b) or "r, g, b" — as #RRGGBB, or null. */
export function parseColour(input) {
  const s = String(input || '').trim();
  let m = s.match(/^#?([0-9a-f]{6})$/i);
  if (m) return `#${m[1].toUpperCase()}`;
  m = s.match(/^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i);
  if (m) return `#${m[1]}${m[1]}${m[2]}${m[2]}${m[3]}${m[3]}`.toUpperCase();
  m = s.match(/^(?:rgba?\s*\()?\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})\s*(?:[,/]\s*[\d.]+%?\s*)?\)?$/i);
  if (m && [m[1], m[2], m[3]].every((v) => Number(v) <= 255)) return `#${[m[1], m[2], m[3]].map((v) => Number(v).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
  return null;
}
const rgbOf = (hex) => { const n = parseInt(hex.slice(1), 16); return `rgb(${n >> 16}, ${(n >> 8) & 255}, ${n & 255})`; };

/**
 * A small popover by its button (the element it sits in): drawn over the
 * page, not inside the dialog's scrolling panel — so it is never cut off —
 * and kept within the window (above the button if there's no room below).
 * Closes on a click outside or Escape.
 */
const Popover = ({ open, onClose, children }) => {
  const anchor = useRef(null);
  const box = useRef(null);
  const [at, setAt] = useState(null);
  useLayoutEffect(() => {
    if (!open) { setAt(null); return undefined; }
    const place = () => {
      const button = anchor.current?.parentElement?.getBoundingClientRect();
      const el = box.current;
      if (!button || !el) return;
      const w = el.offsetWidth; const h = el.offsetHeight; const gap = 4; const edge = 8;
      const left = Math.min(Math.max(edge, button.left), window.innerWidth - w - edge);
      const below = button.bottom + gap;
      const top = below + h <= window.innerHeight - edge ? below : Math.max(edge, button.top - gap - h);
      setAt({ left, top });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => {
      if (box.current?.contains(e.target) || anchor.current?.parentElement?.contains(e.target)) return;
      onClose();
    };
    const key = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', key, true);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', key, true); };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <span ref={anchor} hidden />
      {createPortal(
        <div
          ref={box}
          style={{ position: 'fixed', left: at?.left ?? 0, top: at?.top ?? 0, visibility: at ? 'visible' : 'hidden' }}
          className="z-[80] w-72 max-h-[80vh] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl p-2.5 space-y-2 text-xs"
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  );
};

/** Pick any colour: the system picker, or type a hex / RGB value (applied on Enter or leaving the field). */
const CustomColour = ({ value, onPick, label }) => {
  const t = useT();
  const [text, setText] = useState(value || '');
  useEffect(() => { setText(value || ''); }, [value]);
  const parsed = parseColour(text);
  const apply = () => { if (parsed && parsed !== value) onPick(parsed); };
  return (
    <div className="flex items-center gap-1.5">
      <input type="color" value={value || '#FFFFFF'} onChange={(e) => onPick(e.target.value.toUpperCase())} aria-label={label} title={label} className="w-8 h-7 p-0 border border-gray-300 rounded cursor-pointer bg-white" />
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={apply}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }}
        placeholder={t('plateTemplates.colour.typeHint')}
        aria-label={t('plateTemplates.colour.typeHint')}
        aria-invalid={Boolean(text) && !parsed}
        style={{ ...COMPACT, paddingRight: '0.5rem', width: '100%' }}
        className={`input text-xs font-mono ${text && !parsed ? 'border-red-400' : ''}`}
      />
      {value && <span className="text-[10px] text-gray-400 whitespace-nowrap">{rgbOf(value)}</span>}
    </div>
  );
};

/** Swatches to click: `colours` ([hex]) under a small heading. */
const Swatches = ({ title, colours, value, onPick }) => (colours.length ? (
  <div>
    <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">{title}</p>
    <div className="flex flex-wrap gap-1">
      {colours.map((c) => (
        <button key={c} type="button" onClick={() => onPick(c)} title={c} aria-label={c} className={`w-5 h-5 rounded border ${value === c ? 'ring-2 ring-primary-500 ring-offset-1 border-gray-500' : 'border-gray-300'}`} style={{ background: c }} />
      ))}
    </div>
  </div>
) : null);

/**
 * A colour setting as a swatch button with its picker underneath: automatic
 * (the look's own), none (if allowed), the most used colours, a palette, or
 * any colour. value: 'auto' | 'none' | '#RRGGBB'.
 */
const ColourSetting = ({ label, value, onChange, used, allowNone = true, children }) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const hex = parseColour(value);
  const swatch = hex || (value === 'none' ? 'repeating-linear-gradient(45deg, #fff 0 3px, #e5e7eb 3px 6px)' : AUTO_SWATCH);
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} title={`${label}: ${hex || t(`plateTemplates.colour.${value === 'none' ? 'none' : 'auto'}`)}`} className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] ${open ? 'border-primary-400 bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-400'}`}>
        <span className="w-3.5 h-3.5 rounded-full border border-gray-300 flex-shrink-0" style={{ background: swatch }} /> {label}
      </button>
      <Popover open={open} onClose={close}>
        <p className="font-semibold text-gray-800">{label}</p>
        <div className="flex gap-1">
          <button type="button" onClick={() => onChange('auto')} className={`px-2 py-0.5 rounded-full border ${value === 'auto' ? 'bg-gray-800 border-gray-800 text-white' : 'border-gray-200'}`}>{t('plateTemplates.colour.auto')}</button>
          {allowNone && <button type="button" onClick={() => onChange('none')} className={`px-2 py-0.5 rounded-full border ${value === 'none' ? 'bg-gray-800 border-gray-800 text-white' : 'border-gray-200'}`}>{t('plateTemplates.colour.none')}</button>}
        </div>
        {children}
        <Swatches title={t('plateTemplates.colour.used')} colours={used} value={hex} onPick={onChange} />
        <Swatches title={t('plateTemplates.colour.palette')} colours={PALETTE} value={hex} onPick={onChange} />
        <CustomColour value={hex} onPick={onChange} label={label} />
      </Popover>
    </div>
  );
};

/** The words' fill: automatic, a ready look (silver, gold…), or any colour — solid or a top → bottom gradient. */
const FillSetting = ({ style, onChange, used }) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const preset = LINE_COLORS.find(([id]) => id === style.color);
  const custom = style.color === 'custom' && style.fill;
  const swatch = custom ? (style.fill2 ? `linear-gradient(${style.fill}, ${style.fill2})` : style.fill) : preset ? preset[1] : AUTO_SWATCH;
  const base = custom ? style.fill : preset ? preset[2] : '#FFFFFF';
  const pick = (hex) => onChange({ color: 'custom', fill: hex, fill2: custom ? style.fill2 : undefined });
  const label = t('plateTemplates.colour.fill');
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} title={`${label}: ${custom ? [style.fill, style.fill2].filter(Boolean).join(' → ') : t(`plateTemplates.colour.${preset ? preset[0] : 'auto'}`)}`} className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] ${open ? 'border-primary-400 bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-400'}`}>
        <span className="w-3.5 h-3.5 rounded-full border border-gray-300 flex-shrink-0" style={{ background: swatch }} /> {label}
      </button>
      <Popover open={open} onClose={close}>
        <p className="font-semibold text-gray-800">{label}</p>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">{t('plateTemplates.colour.looks')}</p>
          <div className="flex flex-wrap gap-1">
            {[['auto', AUTO_SWATCH], ...LINE_COLORS].map(([id, sw]) => (
              <button key={id} type="button" onClick={() => onChange({ color: id, fill: undefined, fill2: undefined })} title={t(`plateTemplates.colour.${id}`)} aria-label={t(`plateTemplates.colour.${id}`)} className={`w-5 h-5 rounded-full border ${style.color === id ? 'ring-2 ring-primary-500 ring-offset-1 border-gray-500' : 'border-gray-300'}`} style={{ background: sw }} />
            ))}
          </div>
        </div>
        <Swatches title={t('plateTemplates.colour.used')} colours={used} value={custom ? style.fill : null} onPick={pick} />
        <Swatches title={t('plateTemplates.colour.palette')} colours={PALETTE} value={custom ? style.fill : null} onPick={pick} />
        <CustomColour value={custom ? style.fill : null} onPick={pick} label={label} />
        <div className="border-t border-gray-100 pt-2 space-y-1.5">
          <label className="inline-flex items-center gap-1.5 text-gray-700">
            <input type="checkbox" checked={Boolean(custom && style.fill2)} onChange={(e) => onChange(e.target.checked ? { color: 'custom', fill: base, fill2: '#E58E00' } : { fill2: undefined })} />
            {t('plateTemplates.colour.gradient')}
          </label>
          {custom && style.fill2 && (
            <>
              <p className="text-[11px] text-gray-500">{t('plateTemplates.colour.gradientTo')}</p>
              <div className="flex flex-wrap gap-1">
                {[...used.slice(0, 6), ...PALETTE.slice(0, 12)].filter((c, k, all) => all.indexOf(c) === k).map((c) => (
                  <button key={c} type="button" onClick={() => onChange({ fill2: c })} title={c} aria-label={c} className={`w-5 h-5 rounded border ${style.fill2 === c ? 'ring-2 ring-primary-500 ring-offset-1 border-gray-500' : 'border-gray-300'}`} style={{ background: c }} />
                ))}
              </div>
              <CustomColour value={style.fill2} onPick={(c) => onChange({ fill2: c })} label={t('plateTemplates.colour.gradientTo')} />
            </>
          )}
        </div>
      </Popover>
    </div>
  );
};

/** A style's colour setting as the picker shows it: 'auto' when not set. */
const setting = (v) => v || 'auto';
/** A picker's choice into the style: 'auto' leaves it out. */
const toStyle = (v) => (v === 'auto' ? undefined : v);
/** A style without the settings left out (undefined), as it is saved. */
const tidy = (s) => Object.fromEntries(Object.entries(s).filter(([, v]) => v !== undefined));

// ─── Shadows, as in Photoshop's Layer Style → Drop Shadow ──────────────────
// Up to two drop shadows a line (blend mode, colour, opacity, angle,
// distance, spread, size, noise — pixels at 1920×1080), ready-made looks,
// and for 3D words the depth's size and the light's angle.
export const SHADOW_BLENDS = ['normal', 'multiply', 'screen', 'overlay', 'darken', 'lighten', 'color-burn', 'color-dodge', 'hard-light', 'soft-light', 'difference'];
export const MAX_SHADOWS = 2;
export const SHADOW_DEFAULT = { color: '#000000', blend: 'multiply', opacity: 75, angle: 120, distance: 8, spread: 0, size: 8, noise: 0 };
const SHADOW_PRESETS = {
  soft: [{ ...SHADOW_DEFAULT, opacity: 70, angle: 120, distance: 10, spread: 0, size: 18 }],
  hard: [{ ...SHADOW_DEFAULT, color: '#E3262B', blend: 'normal', opacity: 100, angle: 90, distance: 7, spread: 31, size: 0 }],
  long: [{ ...SHADOW_DEFAULT, opacity: 85, angle: 135, distance: 26, spread: 40, size: 6 }],
  glow: [{ ...SHADOW_DEFAULT, color: '#FFD000', blend: 'screen', opacity: 90, distance: 0, spread: 20, size: 24 }],
  double: [
    { ...SHADOW_DEFAULT, color: '#E3262B', blend: 'normal', opacity: 100, angle: 90, distance: 6, spread: 0, size: 0 },
    { ...SHADOW_DEFAULT, opacity: 80, angle: 120, distance: 16, spread: 10, size: 14 },
  ],
};
// [field, min, max, unit] — as Photoshop shows them.
const SHADOW_FIELDS = [['opacity', 0, 100, '%'], ['distance', 0, 150, 'px'], ['spread', 0, 100, '%'], ['size', 0, 100, 'px'], ['noise', 0, 100, '%']];
const DEPTH_MAX = 60;

/** An angle (°, -180..180; 0 = right, 90 = up) to pick with a click or a drag on a dial. */
export const AngleDial = ({ value, onChange, label }) => {
  const ref = useRef(null);
  const pick = (e) => {
    const r = ref.current.getBoundingClientRect();
    const x = e.clientX - (r.left + r.width / 2); const y = e.clientY - (r.top + r.height / 2);
    if (x || y) onChange(Math.round((Math.atan2(-y, x) * 180) / Math.PI));
  };
  const rad = (value * Math.PI) / 180;
  return (
    <svg
      ref={ref} width="34" height="34" viewBox="-17 -17 34 34" role="slider" aria-label={label} aria-valuenow={value} aria-valuemin={-180} aria-valuemax={180} tabIndex={0}
      className="cursor-pointer flex-shrink-0 touch-none"
      onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); pick(e); }}
      onPointerMove={(e) => { if (e.buttons) pick(e); }}
      onKeyDown={(e) => {
        const step = e.shiftKey ? 15 : 1;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); onChange(clampAngle(value - step)); }
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); onChange(clampAngle(value + step)); }
      }}
    >
      <circle r="15" fill="#fff" stroke="#9ca3af" strokeWidth="1.5" />
      <line x1="0" y1="0" x2={Math.cos(rad) * 13} y2={-Math.sin(rad) * 13} stroke="#111827" strokeWidth="1.8" strokeLinecap="round" />
      <circle r="1.8" fill="#111827" />
    </svg>
  );
};
/** An angle kept within -180..180. */
export const clampAngle = (a) => { const v = Math.round(a) % 360; return v > 180 ? v - 360 : v <= -180 ? v + 360 : v; };

/** A number between min and max: a slider and a box to type in. */
const NumberField = ({ label, value, min, max, unit, onChange }) => (
  <label className="flex items-center gap-1.5">
    <span className="w-16 text-gray-600">{label}</span>
    <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} aria-label={label} className="flex-1 min-w-0 accent-primary-600" />
    <input
      type="number" min={min} max={max} value={value} aria-label={label}
      onChange={(e) => { const v = Number(e.target.value); if (e.target.value !== '' && Number.isFinite(v)) onChange(Math.min(max, Math.max(min, Math.round(v)))); }}
      style={{ ...COMPACT, paddingRight: '0.25rem', paddingLeft: '0.3rem', width: '3.4rem' }} className="input text-[11px]"
    />
    <span className="w-4 text-[10px] text-gray-400">{unit}</span>
  </label>
);

/**
 * A line's shadow: automatic (the look's own), none, or drop shadows of its
 * own set like Photoshop's; for 3D words also the depth's size and angle.
 */
const ShadowSetting = ({ style, effect, used, onChange }) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const close = useCallback(() => setOpen(false), []);
  const shadows = Array.isArray(style.shadows) ? style.shadows : [];
  const mode = shadows.length ? 'custom' : style.shadow === 'none' ? 'none' : 'auto';
  const at = Math.min(tab, Math.max(0, shadows.length - 1));
  const sh = shadows[at];
  const setShadows = (list) => onChange({ shadows: list.length ? list : undefined, shadow: undefined });
  const setOne = (change) => setShadows(shadows.map((s, k) => (k === at ? { ...s, ...change } : s)));
  const label = t('plateTemplates.colour.shadow');
  const first = shadows[0];
  const swatch = first ? first.color : style.shadow === 'none' ? 'repeating-linear-gradient(45deg, #fff 0 3px, #e5e7eb 3px 6px)' : parseColour(style.shadow) || AUTO_SWATCH;
  const is3d = effect === '3d';
  const depthAuto = style.depthSize === undefined;
  const chip = (id, active, onClick) => (
    <button key={id} type="button" onClick={onClick} className={`px-2 py-0.5 rounded-full border ${active ? 'bg-gray-800 border-gray-800 text-white' : 'border-gray-200 hover:border-gray-400'}`}>{t(`plateTemplates.shadow.${id}`)}</button>
  );
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} title={label} className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] ${open ? 'border-primary-400 bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-400'}`}>
        <span className="w-3.5 h-3.5 rounded-full border border-gray-300 flex-shrink-0" style={{ background: swatch, boxShadow: first ? '2px 2px 0 rgba(0,0,0,0.35)' : undefined }} /> {label}{shadows.length > 1 ? ' ×2' : ''}
      </button>
      <Popover open={open} onClose={close}>
        <p className="font-semibold text-gray-800">{t('plateTemplates.shadow.title')}</p>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">{t('plateTemplates.shadow.presets')}</p>
          <div className="flex flex-wrap gap-1">
            {chip('auto', mode === 'auto', () => onChange({ shadows: undefined, shadow: undefined }))}
            {chip('none', mode === 'none', () => onChange({ shadows: undefined, shadow: 'none' }))}
            {Object.keys(SHADOW_PRESETS).map((id) => chip(id, false, () => { setTab(0); setShadows(SHADOW_PRESETS[id].map((s) => ({ ...s }))); }))}
          </div>
        </div>

        <div className="border-t border-gray-100 pt-2 space-y-2">
          <div className="flex items-center gap-1 flex-wrap">
            {shadows.map((_, k) => (
              <button key={k} type="button" onClick={() => setTab(k)} className={`px-2 py-0.5 rounded-md border text-[11px] ${k === at ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-gray-200'}`}>{t('plateTemplates.shadow.n', { n: k + 1 })}</button>
            ))}
            {shadows.length < MAX_SHADOWS && (
              <button type="button" onClick={() => { setShadows([...shadows, { ...(shadows[0] ? { ...SHADOW_DEFAULT, distance: 16, size: 14 } : SHADOW_DEFAULT) }]); setTab(shadows.length); }} className="px-2 py-0.5 rounded-md border border-dashed border-gray-300 text-[11px] text-gray-600 hover:border-gray-500">
                + {shadows.length ? t('plateTemplates.shadow.add') : t('plateTemplates.shadow.n', { n: 1 })}
              </button>
            )}
            {sh && (
              <button type="button" onClick={() => { setShadows(shadows.filter((_, k) => k !== at)); setTab(0); }} title={t('plateTemplates.shadow.remove')} aria-label={t('plateTemplates.shadow.remove')} className="ml-auto text-gray-400 hover:text-red-600 px-1"><FiX size={13} /></button>
            )}
          </div>

          {sh && (
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5">
                <span className="w-16 text-gray-600">{t('plateTemplates.shadow.blend')}</span>
                <select value={sh.blend} onChange={(e) => setOne({ blend: e.target.value })} aria-label={t('plateTemplates.shadow.blend')} style={{ ...COMPACT, fontSize: 11, width: '100%' }} className="input text-[11px]">
                  {SHADOW_BLENDS.map((b) => <option key={b} value={b}>{t(`plateTemplates.shadow.blend.${b}`)}</option>)}
                </select>
              </label>
              <div className="flex items-start gap-1.5">
                <span className="w-16 text-gray-600 pt-1">{t('plateTemplates.shadow.colour')}</span>
                <div className="flex-1 min-w-0 space-y-1">
                  <Swatches title={t('plateTemplates.colour.used')} colours={used.slice(0, 8)} value={sh.color} onPick={(c) => setOne({ color: c })} />
                  <Swatches title={t('plateTemplates.colour.palette')} colours={PALETTE.slice(0, 20)} value={sh.color} onPick={(c) => setOne({ color: c })} />
                  <CustomColour value={sh.color} onPick={(c) => setOne({ color: c })} label={t('plateTemplates.shadow.colour')} />
                </div>
              </div>
              <NumberField label={t('plateTemplates.shadow.opacity')} value={sh.opacity} min={0} max={100} unit="%" onChange={(v) => setOne({ opacity: v })} />
              <div className="flex items-center gap-1.5" title={t('plateTemplates.shadow.angleHint')}>
                <span className="w-16 text-gray-600">{t('plateTemplates.shadow.angle')}</span>
                <AngleDial value={sh.angle} onChange={(v) => setOne({ angle: v })} label={t('plateTemplates.shadow.angle')} />
                <input
                  type="number" min={-180} max={180} value={sh.angle} aria-label={t('plateTemplates.shadow.angle')}
                  onChange={(e) => { const v = Number(e.target.value); if (e.target.value !== '' && Number.isFinite(v)) setOne({ angle: clampAngle(v) }); }}
                  style={{ ...COMPACT, paddingRight: '0.25rem', paddingLeft: '0.3rem', width: '3.4rem' }} className="input text-[11px]"
                />
                <span className="text-[10px] text-gray-400">°</span>
              </div>
              {SHADOW_FIELDS.filter(([k]) => k !== 'opacity').map(([k, min, max, unit]) => (
                <NumberField key={k} label={t(`plateTemplates.shadow.${k}`)} value={sh[k]} min={min} max={max} unit={unit} onChange={(v) => setOne({ [k]: v })} />
              ))}
              <p className="text-[10px] text-gray-400">{t('plateTemplates.shadow.hint')}</p>
            </div>
          )}
        </div>

        {is3d && (
          <div className="border-t border-gray-100 pt-2 space-y-1.5">
            <p className="font-semibold text-gray-800">{t('plateTemplates.shadow.depth')}</p>
            <label className="inline-flex items-center gap-1.5 text-gray-700">
              <input type="checkbox" checked={depthAuto && style.depthAngle === undefined} onChange={(e) => onChange(e.target.checked ? { depthSize: undefined, depthAngle: undefined } : { depthSize: 8, depthAngle: 135 })} />
              {t('plateTemplates.shadow.depthAuto')}
            </label>
            {!(depthAuto && style.depthAngle === undefined) && (
              <>
                <NumberField label={t('plateTemplates.shadow.depthSize')} value={style.depthSize ?? 8} min={0} max={DEPTH_MAX} unit="px" onChange={(v) => onChange({ depthSize: v })} />
                <div className="flex items-center gap-1.5">
                  <span className="w-16 text-gray-600">{t('plateTemplates.shadow.depthAngle')}</span>
                  <AngleDial value={style.depthAngle ?? 135} onChange={(v) => onChange({ depthAngle: v })} label={t('plateTemplates.shadow.depthAngle')} />
                  <span className="text-[11px] text-gray-500">{style.depthAngle ?? 135}°</span>
                </div>
              </>
            )}
          </div>
        )}
      </Popover>
    </div>
  );
};

/**
 * A text area's line styles: each line (line break) of its text its size (in
 * pixels, or as large as fits) and colours — fill (solid or gradient),
 * stroke, shadow and, for 3D words, the depth.
 */
const LineStyles = ({ styles, lines, effect, used, onChange }) => {
  const t = useT();
  const count = Math.min(MAX_LINES, Math.max(1, lines));
  const at = (i) => styles[i] || { color: 'auto', size: 'm' };
  const set = (i, change) => {
    const next = Array.from({ length: Math.max(styles.length, i + 1) }, (_, k) => at(k));
    next[i] = tidy({ ...next[i], ...change });
    onChange(next);
  };
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] text-gray-600">{t('plateTemplates.pkg.lineStyles')}</p>
      {Array.from({ length: count }, (_, i) => {
        const s = at(i);
        return (
          <div key={i} className="flex flex-wrap items-center gap-1">
            <span className="text-[11px] text-gray-500 w-11">{t('plateTemplates.pkg.line', { n: i + 1 })}</span>
            <select value={typeof s.size === 'number' ? String(s.size) : s.size} onChange={(e) => set(i, { size: /^\d+$/.test(e.target.value) ? Number(e.target.value) : e.target.value })} aria-label={t('plateTemplates.pkg.lineSize', { n: i + 1 })} title={t('plateTemplates.pkg.sizeHint')} style={{ ...COMPACT, fontSize: 11, paddingRight: '1.6rem' }} className="input text-[11px] py-0.5 w-auto">
              <option value="m">{t('plateTemplates.pkg.sizeAuto')}</option>
              {OLD_SIZES.includes(s.size) && <option value={s.size}>{t('plateTemplates.pkg.sizeOld', { size: s.size.toUpperCase() })}</option>}
              {typeof s.size === 'number' && !FONT_PX.includes(s.size) && <option value={String(s.size)}>{s.size} px</option>}
              {FONT_PX.map((px) => <option key={px} value={String(px)}>{px} px</option>)}
            </select>
            <FillSetting style={s} used={used} onChange={(change) => set(i, change)} />
            <ColourSetting label={t('plateTemplates.colour.stroke')} value={setting(s.stroke)} used={used} onChange={(v) => set(i, { stroke: toStyle(v) })} />
            <ShadowSetting style={s} effect={effect} used={used} onChange={(change) => set(i, change)} />
            {effect === '3d' && <ColourSetting label={t('plateTemplates.colour.depth')} value={setting(s.depth)} allowNone={false} used={used} onChange={(v) => set(i, { depth: toStyle(v) })} />}
          </div>
        );
      })}
      {count < MAX_LINES && <p className="text-[11px] text-gray-400">{t('plateTemplates.pkg.lineHint')}</p>}
    </div>
  );
};

/** The words of a text, each once (a colour goes with a word wherever it appears). */
const wordsOf = (text) => [...new Set(String(text || '').split(/\s+/).filter(Boolean))];

/**
 * Words in their own colour: click a word of the text shown (the selected
 * text's, for a changing area) and pick its colour. A coloured word keeps its
 * colour in every text of the area it appears in.
 */
const WordColours = ({ text, colours, used, onChange }) => {
  const t = useT();
  const [open, setOpen] = useState(null);
  const close = useCallback(() => setOpen(null), []);
  const shown = wordsOf(text);
  const others = Object.keys(colours).filter((w) => !shown.includes(w));
  const set = (word, colour) => {
    const next = { ...colours };
    if (colour) next[word] = colour; else delete next[word];
    onChange(next);
  };
  const chip = (word, faded = false) => (
    <div key={word} className="relative">
      <button type="button" onClick={() => setOpen(open === word ? null : word)} aria-expanded={open === word} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${colours[word] ? 'border-gray-400 font-semibold' : 'border-gray-200 text-gray-600'} ${faded ? 'opacity-60' : ''}`}>
        {colours[word] && <span className="w-2.5 h-2.5 rounded-full border border-gray-300" style={{ background: colours[word] }} />}
        <span style={colours[word] ? { color: colours[word], textShadow: '0 0 1px #0006' } : undefined}>{word}</span>
      </button>
      <Popover open={open === word} onClose={close}>
        <p className="font-semibold text-gray-800">{t('plateTemplates.pkg.wordColour', { word })}</p>
        <Swatches title={t('plateTemplates.colour.used')} colours={used} value={colours[word]} onPick={(c) => set(word, c)} />
        <Swatches title={t('plateTemplates.colour.palette')} colours={PALETTE} value={colours[word]} onPick={(c) => set(word, c)} />
        <CustomColour value={colours[word] || null} onPick={(c) => set(word, c)} label={t('plateTemplates.pkg.wordColour', { word })} />
        {colours[word] && <button type="button" onClick={() => { set(word, null); close(); }} className="text-red-600 underline">{t('plateTemplates.pkg.wordColourClear')}</button>}
      </Popover>
    </div>
  );
  return (
    <div className="space-y-1">
      <p className="text-[11px] text-gray-600">{t('plateTemplates.pkg.wordColours')}</p>
      {shown.length || others.length ? (
        <div className="flex flex-wrap gap-1">
          {shown.slice(0, 40).map((w) => chip(w))}
          {others.map((w) => chip(w, true))}
        </div>
      ) : <p className="text-[11px] text-gray-400">{t('plateTemplates.pkg.wordColoursEmpty')}</p>}
    </div>
  );
};

/**
 * Saved text styles: put one on this area (its font, effect, ink, line
 * styles and word colours), or save this area's look to use on other plates.
 */
const StyleBar = ({ area, library, onApply, onSaved }) => {
  const t = useT();
  const { user } = useAuthStore();
  const [chosen, setChosen] = useState('');
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const mine = (s) => s && (s.createdBy === String(user?._id || user?.id) || MANAGERS.includes(user?.role));
  const current = library.find((s) => s.id === chosen);
  const apply = (id) => {
    setChosen(id);
    const s = library.find((x) => x.id === id);
    if (s) onApply({ font: s.font, effect: s.effect, ink: s.ink, styles: s.styles || [], wordColors: s.wordColors || {} });
  };
  const save = async () => {
    setBusy(true);
    try {
      const look = { name, font: area.font, effect: area.effect, ink: area.ink, styles: area.styles || [], wordColors: area.wordColors || {} };
      // The same name as the chosen style (one the user may change): it is updated; otherwise a new style.
      const update = current && mine(current) && current.name === name.trim();
      const saved = await api.savePlateTextStyle(look, update ? current.id : null);
      setChosen(saved.id);
      setNaming(false);
      onSaved();
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!current || !window.confirm(t('plateTemplates.pkg.styleRemoveConfirm', { name: current.name }))) return;
    try {
      await api.removePlateTextStyle(current.id);
      setChosen('');
      onSaved();
    } catch (e) {
      showError(e);
    }
  };
  if (naming) {
    return (
      <div className="flex items-center gap-1.5">
        <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) { e.preventDefault(); save(); } if (e.key === 'Escape') setNaming(false); }} maxLength={80} autoFocus placeholder={t('plateTemplates.pkg.styleName')} aria-label={t('plateTemplates.pkg.styleName')} style={{ ...COMPACT, paddingRight: '0.5rem' }} className="input text-xs flex-1 min-w-0" />
        <button type="button" onClick={save} disabled={busy || !name.trim()} className="btn btn-primary btn-sm text-xs flex items-center gap-1">{busy ? <FiLoader size={12} className="animate-spin" /> : <FiSave size={12} />} {current && mine(current) && current.name === name.trim() ? t('plateTemplates.pkg.styleUpdate') : t('plateTemplates.save')}</button>
        <button type="button" onClick={() => setNaming(false)} className="btn btn-ghost btn-sm text-xs">{t('plateTemplates.cancel')}</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <select value={chosen} onChange={(e) => apply(e.target.value)} aria-label={t('plateTemplates.pkg.styles')} style={{ ...COMPACT, fontSize: 12 }} className="input text-xs py-1 flex-1 min-w-0">
        <option value="">{t(library.length ? 'plateTemplates.pkg.stylesPick' : 'plateTemplates.pkg.stylesNone')}</option>
        {library.map((s) => <option key={s.id} value={s.id}>{s.name}{s.createdByName ? ` — ${s.createdByName}` : ''}</option>)}
      </select>
      <button type="button" onClick={() => { setName(current?.name || ''); setNaming(true); }} title={t('plateTemplates.pkg.styleSaveHint')} className="btn btn-outline btn-sm text-xs flex items-center gap-1 whitespace-nowrap"><FiSave size={12} /> {t('plateTemplates.pkg.styleSave')}</button>
      {current && mine(current) && <button type="button" onClick={remove} title={t('plateTemplates.remove')} aria-label={t('plateTemplates.remove')} className="btn btn-ghost btn-sm px-1.5 text-red-600"><FiTrash2 size={13} /></button>}
    </div>
  );
};

// ─── Playing plates live ─────────────────────────────────────────────────
// A package's plates play in the browser with the same animation code their
// videos are drawn with (the server sends it): they start in under a second,
// where making a video of each took seconds. Runs in a sandboxed frame.

// In its own scope: the animation code declares names of its own (clamp, …).
const DRIVER = String.raw`(() => {
let run = 0;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const timing = (total) => ({ total, intro: clamp(total * 0.14, 0.5, 1.4), enter: clamp(total * 0.09, 0.45, 0.9), exit: clamp(total * 0.08, 0.4, 0.8), loop: false });
async function play(d, from) {
  const mine = ++run;
  for (let i = from || 0; i < d.plates.length; i++) {
    if (mine !== run) return;
    const plate = d.plates[i];
    document.querySelectorAll('canvas').forEach((c) => c.remove());
    await window.__stage.init({
      ...d.spec, live: true, sequence: true, textAnim: plate.anim,
      backdrop: { url: d.background },
      text: plate.words.map(({ src, ...w }) => ({ ...w, url: src })),
    }, { width: d.size[0], height: d.size[1] });
    if (mine !== run) return;
    parent.postMessage({ type: 'plate', index: i }, '*');
    const seq = timing(plate.seconds);
    const loop = (d.spec.seconds || 10) * 25;
    const start = performance.now();
    await new Promise((done) => {
      const tick = async (now) => {
        if (mine !== run) return done();
        const t = (now - start) / 1000;
        if (t >= plate.seconds) return done();
        const f = t * 25;
        await window.__stage.frame((f % loop) / loop, Math.floor(f), null, { ...seq, t });
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  }
  if (mine === run) parent.postMessage({ type: 'end' }, '*');
}
window.addEventListener('message', (e) => {
  if (e.data && e.data.type === 'play') play(e.data.data, e.data.from);
  if (e.data && e.data.type === 'stop') run++;
});
parent.postMessage({ type: 'ready' }, '*');
})();
`;

/**
 * Plays \`data\` (from api.platePackageLive) once through: onIndex(i) as plate
 * i starts, onEnd() after the last. Changing \`playKey\` plays it again.
 */
export const LivePlayer = ({ data, playKey = 0, onIndex, onEnd }) => {
  const frame = useRef(null);
  const ready = useRef(false);
  const handlers = useRef({ onIndex, onEnd });
  handlers.current = { onIndex, onEnd };
  // The page: the animation code, then the driver. "</script" in either can't end the tag early.
  const html = useMemo(() => {
    const safe = (code) => code.replace(/<\/script/gi, '<\\/script');
    return '<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;height:100%;background:#000;overflow:hidden}</style></head><body>'
      + '<script>' + safe(data.stage) + '</script><script>' + safe(DRIVER) + '</script></body></html>';
  }, [data.stage]);
  const send = useCallback(() => frame.current?.contentWindow?.postMessage({ type: 'play', data, from: 0 }, '*'), [data]);
  useEffect(() => {
    const onMessage = (e) => {
      if (e.source !== frame.current?.contentWindow) return;
      if (e.data?.type === 'ready') { ready.current = true; send(); }
      if (e.data?.type === 'plate') handlers.current.onIndex?.(e.data.index);
      if (e.data?.type === 'end') handlers.current.onEnd?.();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [send]);
  // New plates, or "play again": start from the first.
  useEffect(() => { if (ready.current) send(); }, [send, playKey]);
  useEffect(() => () => frame.current?.contentWindow?.postMessage({ type: 'stop' }, '*'), []);
  return <iframe ref={frame} title="plate" sandbox="allow-scripts" srcDoc={html} className="absolute inset-0 w-full h-full border-0" />;
};

// ─── The package dialog ──────────────────────────────────────────────────

let keys = 0;
const newItem = (it = {}) => ({
  key: `i${++keys}`, texts: [it.texts?.[0] || '', it.texts?.[1] || ''], anim: it.anim || '', font: it.font || '', seconds: it.seconds || '',
});
const lineCount = (text) => String(text || '').split('\n').length;

/** Where a second text area goes at first: a strip above the panel (a heading), or below it if there's no room. */
function secondBox(box) {
  const h = 0.1;
  const above = box.y - h - 0.03;
  if (above >= 0.05) return { x: box.x, y: Number(above.toFixed(3)), w: box.w, h };
  return { x: box.x, y: Number(Math.min(0.85, box.y + box.h + 0.03).toFixed(3)), w: box.w, h };
}

/** The package as the server takes it. */
const bodyOf = ({ name, areas, items, seconds, anim, blurs }) => ({
  name,
  seconds,
  anim,
  blurs,
  areas: areas.map(({
    box, role, text, ink, font, effect, styles, wordColors,
  }) => ({ box, role, text: role === 'common' ? text : '', ink, font, effect, styles, wordColors })),
  items: items.map((it) => ({
    texts: areas.map((a, i) => (a.role === 'changes' ? it.texts[i] : '')),
    anim: it.anim || undefined,
    font: it.font || undefined,
    seconds: it.seconds || undefined,
  })),
});

export const PackageDialog = ({ plate, onClose }) => {
  const t = useT();
  const lang = useLanguageStore((s) => s.lang);
  const firstArea = useMemo(() => ({
    box: plate.box, role: 'changes', text: '', ink: plate.ink || 'auto', font: plate.font || 'pallavi', effect: plate.effect || 'flat', styles: [], wordColors: {},
  }), [plate]);
  const [areas, setAreas] = useState([firstArea]);
  const [items, setItems] = useState([newItem()]);
  const [seconds, setSeconds] = useState(10);
  const [anim, setAnim] = useState('wipe');
  const [blurs, setBlurs] = useState([]);
  const [name, setName] = useState('');
  const [packageId, setPackageId] = useState(null);
  const [saved, setSaved] = useState([]);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [selected, setSelected] = useState(0);
  const [view, setView] = useState('preview');
  const [showText, setShowText] = useState(true);
  // A live preview playing: { n (a text's number, or 'all'), data, error, index (playing), ended, key }.
  const [video, setVideo] = useState(null);
  const [pasting, setPasting] = useState(false);
  const [pasted, setPasted] = useState('');
  const [fonts, setFonts] = useState([]);
  // Saved text styles (everyone's) and the colours used most.
  const [library, setLibrary] = useState({ styles: [], usedColors: [] });
  const exporter = useExport();
  const focusKey = useRef(null);

  useEffect(() => { loadFonts().then(setFonts).catch(showError); }, []);
  // A server without text styles yet (404): the package works as before, without them.
  const loadLibrary = useCallback(() => api.plateTextStyles().then(setLibrary).catch((e) => { if (e?.response?.status !== 404) showError(e); }), []);
  useEffect(() => { loadLibrary(); }, [loadLibrary]);
  const loadSaved = useCallback(() => api.platePackages(plate.id).then(setSaved).catch(showError), [plate.id]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const changing = areas.map((a, i) => (a.role === 'changes' ? i : -1)).filter((i) => i >= 0);
  const hasChanging = changing.length > 0;
  const filled = items.filter((it) => changing.some((i) => it.texts[i]?.trim())).length;
  const index = Math.min(selected, items.length - 1);
  const current = items[index] || items[0];
  const body = () => bodyOf({ name, areas, items, seconds, anim, blurs });
  const areasBody = bodyOf({ areas, items: [], seconds, anim, blurs }).areas;

  // The quick preview: the selected text on the plate (or the plate alone).
  const preview = useStillPreview(plate.id, {
    areas: areasBody, texts: hasChanging ? current?.texts || [] : [], font: current?.font || undefined, blurs, noText: !showText,
  }, view === 'preview' && !video);

  // A new row's first field takes the cursor.
  useEffect(() => {
    if (!focusKey.current) return;
    document.getElementById(`pkg-${focusKey.current}-${changing[0]}`)?.focus();
    focusKey.current = null;
  });

  const setArea = (i, change) => setAreas((now) => now.map((a, k) => (k === i ? { ...a, ...change } : a)));
  const addArea = () => setAreas((now) => (now.length >= 2 ? now : [...now, { ...firstArea, box: secondBox(now[0].box), role: 'common', effect: '3d' }]));
  const removeArea = (i) => {
    setAreas((now) => now.filter((_, k) => k !== i));
    setItems((now) => now.map((it) => ({ ...it, texts: i === 0 ? [it.texts[1], ''] : [it.texts[0], ''] })));
  };
  const setItem = (key, change) => setItems((now) => now.map((it) => (it.key === key ? { ...it, ...change } : it)));
  const setText = (key, i, value) => setItems((now) => now.map((it) => (it.key === key ? { ...it, texts: it.texts.map((v, k) => (k === i ? value : v)) } : it)));
  const addItem = (after = items.length - 1) => {
    if (items.length >= MAX_ITEMS) return;
    const item = newItem();
    focusKey.current = item.key;
    setItems((now) => [...now.slice(0, after + 1), item, ...now.slice(after + 1)]);
    setSelected(after + 1);
  };
  const removeItem = (key) => setItems((now) => (now.length > 1 ? now.filter((it) => it.key !== key) : [newItem()]));

  // Paste a list: one text per line (with two changing areas: "first | second").
  const addPasted = () => {
    const lines = pasted.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return;
    const fresh = lines.map((line) => {
      const parts = changing.length > 1 ? line.split('|').map((s) => s.trim()) : [line];
      const texts = ['', ''];
      changing.forEach((areaIndex, k) => { texts[areaIndex] = (parts[k] || '').slice(0, MAX_TEXT); });
      return newItem({ texts });
    });
    setItems((now) => {
      const kept = now.filter((it) => changing.some((i) => it.texts[i]?.trim()));
      const room = MAX_ITEMS - kept.length;
      if (fresh.length > room) showError(new Error(t('plateTemplates.pkg.tooMany', { count: MAX_ITEMS })));
      return [...kept, ...fresh.slice(0, room)];
    });
    setPasted('');
    setPasting(false);
  };

  const savePackage = async () => {
    setSaving(true);
    try {
      const made = await api.savePlatePackage(plate.id, body(), packageId);
      setPackageId(made.id);
      setName(made.name);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
      loadSaved();
      loadLibrary();
    } catch (e) {
      showError(e);
    } finally {
      setSaving(false);
    }
  };

  const open = (id) => {
    setVideo(null);
    if (!id) {
      setPackageId(null); setName(''); setAreas([firstArea]); setItems([newItem()]); setSeconds(10); setAnim('wipe'); setBlurs([]); setSelected(0);
      return;
    }
    const p = saved.find((s) => s.id === id);
    if (!p) return;
    setPackageId(p.id);
    setName(p.name);
    setSeconds(p.seconds || 10);
    setAnim(p.anim || 'wipe');
    setBlurs(p.blurs || []);
    setAreas(p.areas.map((a) => ({ ...firstArea, ...a, styles: a.styles || [], wordColors: a.wordColors || {} })));
    setItems(p.items.length ? p.items.map((it) => newItem({ ...it, seconds: it.seconds || '' })) : [newItem()]);
    setSelected(0);
  };

  const removeSaved = async () => {
    if (!packageId || !window.confirm(t('plateTemplates.pkg.removeConfirm'))) return;
    try {
      await api.removePlatePackage(packageId);
      open(null);
      loadSaved();
    } catch (e) {
      showError(e);
    }
  };

  const download = (kind, n = null) => exporter.start(plate.id, { kind, package: body(), ...(n ? { items: [n] } : {}) });
  // Play plates live: one text's (k), or every text's one after another ('all') —
  // each with its own animation and length, exactly as its video. No video is made.
  const playLive = async (n, items) => {
    setView('preview');
    setVideo({ n, data: null, error: '', index: 0, ended: false, key: 0 });
    try {
      const data = await api.platePackageLive(plate.id, body(), items);
      setVideo((v) => (v && v.n === n ? { ...v, data } : v));
    } catch (e) {
      setVideo((v) => (v && v.n === n ? { ...v, error: e?.response?.data?.message || e.message } : v));
    }
  };
  const playVideo = (k) => { setSelected(k); playLive(k + 1, hasChanging ? [k + 1] : null); };
  const playAll = () => playLive('all', null);
  const ready = hasChanging ? filled > 0 : areas.some((a) => a.text.trim());
  const areaLabel = (i) => t('plateTemplates.pkg.area', { n: i + 1 });
  const boxes = [
    ...areas.map((a, i) => ({ box: a.box, label: `${areaLabel(i)} · ${t(`plateTemplates.pkg.role.${a.role}`)}` })),
    ...blurBoxes(blurs, t),
  ];
  const moveBox = (i, box) => {
    if (i < areas.length) setArea(i, { box });
    else setBlurs((now) => now.map((b, k) => (k === i - areas.length ? { ...b, ...box } : b)));
  };
  const linesIn = (i) => Math.max(lineCount(areas[i].text), ...items.map((it) => lineCount(it.texts[i])));
  const fontName = (id) => { const f = fonts.find((x) => x.id === id); return f ? (lang === 'te' ? f.te : f.name) : id; };
  const textKeys = (key, k) => (e) => {
    // Enter: the next text; Shift+Enter: a new line in this text (each line can be styled).
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addItem(k); }
  };

  return (
    <div className="fixed inset-0 z-[75] bg-black/70 flex items-center justify-center p-2 sm:p-4" role="dialog" aria-modal="true" aria-label={t('plateTemplates.pkg.title')}>
      <div className="w-full max-w-6xl max-h-[96vh] overflow-y-auto bg-white rounded-2xl shadow-2xl p-4 sm:p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><FiPackage size={18} className="text-primary-600" /> {t('plateTemplates.pkg.title')}</h2>
            <p className="text-sm text-gray-500">{t('plateTemplates.pkg.hint', { count: MAX_ITEMS })}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={packageId || ''} onChange={(e) => open(e.target.value)} style={COMPACT} className="input text-sm py-1.5 w-auto max-w-[16rem]" aria-label={t('plateTemplates.pkg.saved')}>
              <option value="">{t('plateTemplates.pkg.new')}</option>
              {saved.map((p) => <option key={p.id} value={p.id}>{p.name}{p.createdByName ? ` — ${p.createdByName}` : ''}</option>)}
            </select>
            <button type="button" onClick={onClose} aria-label={t('plateTemplates.close')} className="btn btn-ghost btn-sm"><FiX size={18} /></button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* The plate: the preview of the selected text, or the areas to move. */}
          <div className="lg:col-span-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div role="tablist" className="inline-flex p-0.5 rounded-lg bg-gray-100 text-xs font-medium">
                {['preview', 'areas'].map((v) => (
                  <button key={v} type="button" role="tab" aria-selected={view === v} onClick={() => { setView(v); setVideo(null); }} className={`px-3 py-1 rounded-md ${view === v ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                    {t(`plateTemplates.pkg.view.${v}`)}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {hasChanging && filled > 1 && (
                  <button type="button" onClick={playAll} disabled={Boolean(video && !video.data && !video.error)} className="btn btn-outline btn-sm text-xs flex items-center gap-1"><FiPlay size={13} /> {t('plateTemplates.pkg.previewAll', { count: filled })}</button>
                )}
                {view === 'preview' && !video && <TextToggle on={showText} onChange={setShowText} />}
              </div>
              {video && <button type="button" onClick={() => setVideo(null)} className="btn btn-ghost btn-sm text-xs flex items-center gap-1"><FiImage size={13} /> {t('plateTemplates.pkg.backToPicture')}</button>}
            </div>
            {view === 'areas' ? (
              <BoxesEditor src={api.plateTemplateUrl(plate.id, 'source.jpg', plate.updatedAt)} boxes={boxes} onChange={moveBox} />
            ) : video ? (
              <div className="relative aspect-video bg-gray-900 rounded-xl overflow-hidden">
                {video.data ? (
                  <>
                    <LivePlayer
                      data={video.data}
                      playKey={video.key}
                      onIndex={(index) => setVideo((v) => (v ? { ...v, index, ended: false } : v))}
                      onEnd={() => setVideo((v) => (v ? { ...v, ended: true } : v))}
                    />
                    <span className="absolute top-2 left-2 rounded-full bg-black/65 text-white text-[11px] px-2.5 py-0.5">
                      {video.data.plates.length > 1
                        ? t('plateTemplates.pkg.playingOf', { n: video.data.plates[video.index]?.n, at: video.index + 1, count: video.data.plates.length })
                        : t('plateTemplates.pkg.showing', { n: video.data.plates[0]?.n })}
                    </span>
                    <button type="button" onClick={() => setVideo((v) => ({ ...v, key: v.key + 1, index: 0, ended: false }))} className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/70 hover:bg-black/85 text-white text-xs px-3 py-1">
                      <FiPlay size={12} /> {t(video.ended ? 'plateTemplates.pkg.playAgain' : 'plateTemplates.pkg.restart')}
                    </button>
                  </>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/85 text-sm">
                    {video.error
                      ? <><FiAlertTriangle size={20} className="text-amber-400" /> {video.error}</>
                      : <><FiLoader size={20} className="animate-spin" /> {t('plateTemplates.pkg.gettingReady')}</>}
                  </div>
                )}
              </div>
            ) : (
              <StillPreview preview={preview} label={t('plateTemplates.pkg.view.preview')}>
                {hasChanging && showText && <span className="absolute top-2 left-2 rounded-full bg-black/65 text-white text-[11px] px-2.5 py-0.5">{t('plateTemplates.pkg.showing', { n: index + 1 })}</span>}
              </StillPreview>
            )}
            <p className="text-xs text-gray-500">{t(view === 'areas' ? 'plateTemplates.pkg.areasHint' : 'plateTemplates.pkg.previewHint')}</p>
            <BlurControls blurs={blurs} onChange={setBlurs} onAdded={() => { setView('areas'); setVideo(null); }} />
          </div>

          {/* The text areas: common or changing, and how their words look. */}
          <div className="lg:col-span-2 space-y-3">
            {areas.map((a, i) => (
              <fieldset key={i} className={`rounded-xl border p-3 space-y-2 ${i === 0 ? 'border-primary-200' : 'border-amber-200'}`}>
                <div className="flex items-center justify-between gap-2">
                  <legend className="text-sm font-semibold text-gray-900">{areaLabel(i)}</legend>
                  {areas.length > 1 && (
                    <button type="button" onClick={() => removeArea(i)} className="btn btn-ghost btn-sm text-red-600 text-xs flex items-center gap-1"><FiTrash2 size={13} /> {t('plateTemplates.pkg.removeArea')}</button>
                  )}
                </div>
                <div role="radiogroup" aria-label={t('plateTemplates.pkg.roleLabel')} className="grid grid-cols-2 gap-1 p-0.5 rounded-lg bg-gray-100 text-xs font-medium">
                  {['changes', 'common'].map((r) => (
                    <button key={r} type="button" role="radio" aria-checked={a.role === r} onClick={() => setArea(i, { role: r })} className={`px-2 py-1.5 rounded-md ${a.role === r ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                      {t(`plateTemplates.pkg.role.${r}`)}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-500">{t(`plateTemplates.pkg.roleHint.${a.role}`)}</p>
                <StyleBar area={a} library={library.styles} onApply={(look) => setArea(i, look)} onSaved={loadLibrary} />
                {a.role === 'common' && (
                  <textarea value={a.text} onChange={(e) => setArea(i, { text: e.target.value })} maxLength={MAX_TEXT} rows={Math.min(MAX_LINES, lineCount(a.text))} placeholder={t('plateTemplates.pkg.commonPlaceholder')} className="input text-sm resize-none" />
                )}
                <div className="grid grid-cols-3 gap-2">
                  <label className="block text-[11px] text-gray-600">
                    {t('plateTemplates.font')}
                    <select value={a.font} onChange={(e) => setArea(i, { font: e.target.value })} className="input mt-0.5 text-xs py-1">
                      {fonts.map((f) => <option key={f.id} value={f.id}>{lang === 'te' ? f.te : f.name}</option>)}
                    </select>
                  </label>
                  <label className="block text-[11px] text-gray-600">
                    {t('plateTemplates.textStyle')}
                    <select value={a.effect} onChange={(e) => setArea(i, { effect: e.target.value })} className="input mt-0.5 text-xs py-1">
                      {EFFECTS.map((v) => <option key={v} value={v}>{t(`plateTemplates.effect.${v}`)}</option>)}
                    </select>
                  </label>
                  <label className="block text-[11px] text-gray-600">
                    {t('plateTemplates.editInk')}
                    <select value={a.ink} onChange={(e) => setArea(i, { ink: e.target.value })} className="input mt-0.5 text-xs py-1">
                      {INKS.map((v) => <option key={v} value={v}>{v === 'auto' ? t('plateTemplates.pkg.inkAuto') : t(`plateTemplates.ink.${v}`)}</option>)}
                    </select>
                  </label>
                </div>
                <LineStyles styles={a.styles || []} lines={linesIn(i)} effect={a.effect} used={library.usedColors} onChange={(styles) => setArea(i, { styles })} />
                <WordColours text={a.role === 'common' ? a.text : current?.texts[i]} colours={a.wordColors || {}} used={library.usedColors} onChange={(wordColors) => setArea(i, { wordColors })} />
              </fieldset>
            ))}
            {areas.length < 2 && (
              <button type="button" onClick={() => { addArea(); setView('areas'); setVideo(null); }} className="btn btn-outline btn-sm w-full flex items-center justify-center gap-1.5"><FiPlus size={14} /> {t('plateTemplates.pkg.addArea')}</button>
            )}
          </div>
        </div>

        {/* The texts: one plate each, each with its own animation, font and length if wanted. */}
        {hasChanging ? (
          <section className="space-y-2" aria-label={t('plateTemplates.pkg.texts')}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-gray-900">
                {t('plateTemplates.pkg.texts')} <span className="font-normal text-gray-500">{t('plateTemplates.pkg.count', { count: filled, max: MAX_ITEMS })}</span>
              </p>
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                <label className="inline-flex items-center gap-1">
                  {t('plateTemplates.pkg.defaultAnim')}
                  <select value={anim} onChange={(e) => setAnim(e.target.value)} style={COMPACT} className="input text-xs py-1 w-auto">
                    {ANIMS.map((v) => <option key={v} value={v}>{t(`plateTemplates.anim.${v}`)}</option>)}
                  </select>
                </label>
                <label className="inline-flex items-center gap-1">
                  {t('plateTemplates.pkg.seconds')}
                  <select value={seconds} onChange={(e) => setSeconds(Number(e.target.value))} style={COMPACT} className="input text-xs py-1 w-auto">
                    {SECONDS.map((s) => <option key={s} value={s}>{t('plateTemplates.pkg.secondsValue', { seconds: s })}</option>)}
                  </select>
                </label>
                <button type="button" onClick={() => setPasting((v) => !v)} aria-expanded={pasting} className="btn btn-ghost btn-sm flex items-center gap-1 text-xs"><FiClipboard size={13} /> {t('plateTemplates.pkg.paste')}</button>
              </div>
            </div>
            {pasting && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2">
                <p className="text-xs text-gray-600">{t(changing.length > 1 ? 'plateTemplates.pkg.pasteHintTwo' : 'plateTemplates.pkg.pasteHint', { count: MAX_ITEMS })}</p>
                <textarea value={pasted} onChange={(e) => setPasted(e.target.value)} rows={6} className="input w-full text-sm resize-y" autoFocus />
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setPasting(false)} className="btn btn-secondary btn-sm">{t('plateTemplates.cancel')}</button>
                  <button type="button" onClick={addPasted} disabled={!pasted.trim()} className="btn btn-primary btn-sm">{t('plateTemplates.pkg.pasteGo')}</button>
                </div>
              </div>
            )}
            <ol className="space-y-1.5">
              {items.map((it, k) => {
                const n = k + 1;
                const has = changing.some((i) => it.texts[i]?.trim());
                return (
                  <li key={it.key} className={`rounded-lg border px-2 py-1.5 ${k === index ? 'border-primary-300 bg-primary-50/50' : 'border-gray-200'}`}>
                    <div className="flex items-start gap-2">
                      <span className="w-6 pt-2 text-xs text-gray-500 tabular-nums text-right flex-shrink-0">{n}</span>
                      <div className={`flex-1 grid gap-1.5 ${changing.length > 1 ? 'sm:grid-cols-2' : ''}`}>
                        {changing.map((i) => (
                          <textarea
                            key={i}
                            id={`pkg-${it.key}-${i}`}
                            value={it.texts[i]}
                            onChange={(e) => setText(it.key, i, e.target.value)}
                            onFocus={() => { setSelected(k); setVideo(null); }}
                            onKeyDown={textKeys(it.key, k)}
                            maxLength={MAX_TEXT}
                            rows={Math.min(MAX_LINES, lineCount(it.texts[i]))}
                            placeholder={changing.length > 1 ? areaLabel(i) : t('plateTemplates.pkg.itemPlaceholder', { n })}
                            aria-label={`${t('plateTemplates.pkg.item', { n })} — ${areaLabel(i)}`}
                            className="input text-sm py-1.5 resize-none"
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0 pt-0.5">
                        <button type="button" onClick={() => { setSelected(k); setView('preview'); setVideo(null); }} title={t('plateTemplates.pkg.show')} aria-label={t('plateTemplates.pkg.show')} className="btn btn-ghost btn-sm px-1.5"><FiEye size={14} /></button>
                        <button type="button" onClick={() => playVideo(k)} disabled={!has} title={t('plateTemplates.pkg.playVideo')} aria-label={t('plateTemplates.pkg.playVideo')} className="btn btn-ghost btn-sm px-1.5"><FiPlay size={14} /></button>
                        <button type="button" onClick={() => download('images', n)} disabled={!has || exporter.busy} title={t('plateTemplates.pkg.oneImage')} aria-label={t('plateTemplates.pkg.oneImage')} className="btn btn-ghost btn-sm px-1.5"><FiImage size={14} /></button>
                        <button type="button" onClick={() => download('videos', n)} disabled={!has || exporter.busy} title={t('plateTemplates.pkg.oneVideo')} aria-label={t('plateTemplates.pkg.oneVideo')} className="btn btn-ghost btn-sm px-1.5"><FiFilm size={14} /></button>
                        <button type="button" onClick={() => removeItem(it.key)} title={t('plateTemplates.pkg.removeItem')} aria-label={t('plateTemplates.pkg.removeItem')} className="btn btn-ghost btn-sm px-1.5 text-red-600"><FiTrash2 size={14} /></button>
                      </div>
                    </div>
                    {/* This text's own animation, font and length — the package's unless changed. */}
                    <div className="flex flex-wrap items-center gap-1.5 pl-8 pt-1 text-[11px] text-gray-600">
                      <select value={it.anim} onChange={(e) => setItem(it.key, { anim: e.target.value })} aria-label={t('plateTemplates.pkg.itemAnim', { n })} style={{ ...COMPACT, fontSize: 11, maxWidth: '15rem' }} className={`input text-[11px] py-0.5 w-auto ${it.anim ? 'border-primary-300' : ''}`}>
                        <option value="">{t('plateTemplates.pkg.animDefault', { anim: t(`plateTemplates.anim.${anim}`) })}</option>
                        {ANIMS.map((v) => <option key={v} value={v}>{t(`plateTemplates.anim.${v}`)}</option>)}
                      </select>
                      <select value={it.font} onChange={(e) => setItem(it.key, { font: e.target.value })} aria-label={t('plateTemplates.pkg.itemFont', { n })} style={{ ...COMPACT, fontSize: 11, maxWidth: '15rem' }} className={`input text-[11px] py-0.5 w-auto max-w-[14rem] ${it.font ? 'border-primary-300' : ''}`}>
                        <option value="">{t('plateTemplates.pkg.fontDefault', { font: fontName(areas[changing[0]]?.font) })}</option>
                        {fonts.map((f) => <option key={f.id} value={f.id}>{lang === 'te' ? f.te : f.name}</option>)}
                      </select>
                      <select value={it.seconds} onChange={(e) => setItem(it.key, { seconds: e.target.value ? Number(e.target.value) : '' })} aria-label={t('plateTemplates.pkg.itemSeconds', { n })} style={{ ...COMPACT, fontSize: 11, maxWidth: '15rem' }} className={`input text-[11px] py-0.5 w-auto ${it.seconds ? 'border-primary-300' : ''}`}>
                        <option value="">{t('plateTemplates.pkg.secondsDefault', { seconds })}</option>
                        {SECONDS.map((s) => <option key={s} value={s}>{t('plateTemplates.pkg.secondsValue', { seconds: s })}</option>)}
                      </select>
                    </div>
                  </li>
                );
              })}
            </ol>
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" onClick={() => addItem()} disabled={items.length >= MAX_ITEMS} className="btn btn-outline btn-sm flex items-center gap-1.5">
                <FiPlus size={14} /> {items.length >= MAX_ITEMS ? t('plateTemplates.pkg.full', { count: MAX_ITEMS }) : t('plateTemplates.pkg.addText')}
              </button>
              <p className="text-[11px] text-gray-500">{t('plateTemplates.pkg.keysHint')}</p>
            </div>
          </section>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-gray-600 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">{t('plateTemplates.pkg.allCommon')}</p>
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
              <label className="inline-flex items-center gap-1">
                {t('plateTemplates.pkg.defaultAnim')}
                <select value={anim} onChange={(e) => setAnim(e.target.value)} style={COMPACT} className="input text-xs py-1 w-auto">
                  {ANIMS.map((v) => <option key={v} value={v}>{t(`plateTemplates.anim.${v}`)}</option>)}
                </select>
              </label>
              <label className="inline-flex items-center gap-1">
                {t('plateTemplates.pkg.seconds')}
                <select value={seconds} onChange={(e) => setSeconds(Number(e.target.value))} style={COMPACT} className="input text-xs py-1 w-auto">
                  {SECONDS.map((s) => <option key={s} value={s}>{t('plateTemplates.pkg.secondsValue', { seconds: s })}</option>)}
                </select>
              </label>
              <button type="button" onClick={() => playVideo(0)} disabled={!ready} className="btn btn-outline btn-sm flex items-center gap-1"><FiPlay size={13} /> {t('plateTemplates.pkg.playVideo')}</button>
            </div>
          </div>
        )}

        {/* Saving, and downloading every plate. */}
        <div className="flex flex-wrap items-end justify-between gap-3 border-t border-gray-100 pt-3">
          <div className="flex flex-wrap items-end gap-2">
            <label className="block text-xs text-gray-600">
              {t('plateTemplates.pkg.name')}
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder={t('plateTemplates.pkg.namePlaceholder')} className="input mt-0.5 text-sm py-1.5 w-56" />
            </label>
            <button type="button" onClick={savePackage} disabled={saving} className="btn btn-outline btn-sm flex items-center gap-1.5">
              {saving ? <FiLoader size={14} className="animate-spin" /> : justSaved ? <FiCheck size={14} className="text-green-600" /> : <FiSave size={14} />}
              {justSaved ? t('plateTemplates.pkg.saved') : t('plateTemplates.save')}
            </button>
            {packageId && (
              <button type="button" onClick={removeSaved} className="btn btn-ghost btn-sm text-red-600 flex items-center gap-1"><FiTrash2 size={14} /> {t('plateTemplates.remove')}</button>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <button type="button" onClick={() => download('images')} disabled={!ready || exporter.busy} className="btn btn-outline btn-sm flex items-center gap-1.5">
              <FiImage size={14} /> {t(filled > 1 ? 'plateTemplates.pkg.allImages' : 'plateTemplates.pkg.image', { count: filled })}
            </button>
            <button type="button" onClick={() => download('videos')} disabled={!ready || exporter.busy} className={`btn ${filled > 1 ? 'btn-outline' : 'btn-primary'} btn-sm flex items-center gap-1.5`}>
              <FiFilm size={14} /> {t(filled > 1 ? 'plateTemplates.pkg.allVideos' : 'plateTemplates.pkg.video', { count: filled })}
            </button>
            {filled > 1 && (
              <button type="button" onClick={() => download('combined')} disabled={!ready || exporter.busy} className="btn btn-primary btn-sm flex items-center gap-1.5">
                <FiFilm size={14} /> {t('plateTemplates.pkg.oneVideoAll', { count: filled })}
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] text-gray-500">{t('plateTemplates.pkg.format')}</p>
          <ExportStatus job={exporter.job} onClear={exporter.clear} />
        </div>
      </div>
    </div>
  );
};
